import { useState } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "wagmi";
import { useAllMarkets, useFactoryOwner, useResolveMarket, useCloseMarket } from "@/hooks/useMarkets";
import { formatUSDT } from "@/lib/contracts";

export default function AdminPage() {
  const { address, isConnected } = useAccount();
  const { data: ownerAddress } = useFactoryOwner();
  const { markets, isLoading } = useAllMarkets();

  const isOwner = isConnected && address && ownerAddress && address.toLowerCase() === ownerAddress.toLowerCase();

  if (!isConnected) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Panel</h2>
          <p className="text-gray-600">Connect your wallet to access the admin panel.</p>
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">Only the contract owner can access this page.</p>
          <p className="text-sm text-gray-500">
            Connected: <span className="font-mono">{address?.slice(0, 6)}...{address?.slice(-4)}</span>
          </p>
          <p className="text-sm text-gray-500">
            Owner: <span className="font-mono">{ownerAddress?.slice(0, 6)}...{ownerAddress?.slice(-4)}</span>
          </p>
        </div>
      </div>
    );
  }

  // Filter markets that can be resolved (expired but not resolved)
  const resolvableMarkets = markets.filter((market) => {
    const endDate = new Date(Number(market.endTime) * 1000);
    const isExpired = endDate < new Date();
    return isExpired && !market.isResolved;
  });

  // Already resolved markets
  const resolvedMarkets = markets.filter((market) => market.isResolved);

  // Active markets (not expired, not resolved)
  const activeMarkets = markets.filter((market) => {
    const endDate = new Date(Number(market.endTime) * 1000);
    const isExpired = endDate < new Date();
    return !isExpired && !market.isResolved;
  });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-600 text-sm mt-1">Manage and resolve prediction markets</p>
        </div>
        <div className="badge badge-green">Owner Connected</div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="card p-4 text-center">
          <div className="text-3xl font-bold text-primary-600">{activeMarkets.length}</div>
          <div className="text-sm text-gray-600">Active Markets</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-bold text-yellow-600">{resolvableMarkets.length}</div>
          <div className="text-sm text-gray-600">Pending Resolution</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-bold text-green-600">{resolvedMarkets.length}</div>
          <div className="text-sm text-gray-600">Resolved</div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="card p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading markets...</p>
        </div>
      )}

      {/* Pending Resolution */}
      {resolvableMarkets.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
            Pending Resolution ({resolvableMarkets.length})
          </h2>
          <div className="space-y-4">
            {resolvableMarkets.map((market) => (
              <ResolvableMarketCard key={market.address} market={market} />
            ))}
          </div>
        </div>
      )}

      {/* No Pending Markets */}
      {!isLoading && resolvableMarkets.length === 0 && (
        <div className="card p-6 text-center mb-8 bg-gray-50">
          <p className="text-gray-600">No markets pending resolution.</p>
          <p className="text-sm text-gray-500 mt-1">Markets become resolvable after their end time.</p>
        </div>
      )}

      {/* Resolved Markets */}
      {resolvedMarkets.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            Resolved Markets ({resolvedMarkets.length})
          </h2>
          <div className="space-y-2">
            {resolvedMarkets.map((market) => (
              <div key={market.address} className="card p-4">
                <div className="flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/market/${market.address}`}
                      className="font-medium text-gray-900 hover:text-primary-600 truncate block"
                    >
                      {market.question}
                    </Link>
                    <div className="text-sm text-gray-500 mt-1">
                      Pool: {formatUSDT(market.totalYesBets + market.totalNoBets)} USDT
                    </div>
                  </div>
                  <span className={`badge ml-4 ${market.winningOutcome ? "badge-green" : "badge-red"}`}>
                    {market.winningOutcome ? "YES Won" : "NO Won"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Markets */}
      {activeMarkets.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
            Active Markets ({activeMarkets.length})
          </h2>
          <div className="space-y-4">
            {activeMarkets.map((market) => (
              <ActiveMarketCard key={market.address} market={market} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResolvableMarketCard({ market }: { market: { address: `0x${string}`; question: string; totalYesBets: bigint; totalNoBets: bigint; endTime: bigint } }) {
  const [selectedOutcome, setSelectedOutcome] = useState<boolean | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { resolveMarket, isPending, isSuccess, error, reset } = useResolveMarket();

  const totalPool = market.totalYesBets + market.totalNoBets;
  const yesPercent = totalPool > 0n ? Number((market.totalYesBets * 100n) / totalPool) : 50;
  const endDate = new Date(Number(market.endTime) * 1000);

  const handleResolve = () => {
    if (selectedOutcome !== null) {
      resolveMarket(market.address, selectedOutcome);
    }
  };

  // Reset after success
  if (isSuccess) {
    setTimeout(() => {
      reset();
      setSelectedOutcome(null);
      setShowConfirm(false);
    }, 2000);
  }

  return (
    <div className="card overflow-hidden">
      <div className="p-4">
        {/* Question */}
        <Link to={`/market/${market.address}`} className="hover:text-primary-600">
          <h3 className="font-medium text-gray-900 mb-2">{market.question}</h3>
        </Link>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 text-sm mb-4">
          <div>
            <span className="text-gray-500">Total Pool:</span>
            <span className="font-medium ml-1">{formatUSDT(totalPool)} USDT</span>
          </div>
          <div>
            <span className="text-gray-500">YES:</span>
            <span className="font-medium text-green-600 ml-1">{yesPercent.toFixed(1)}%</span>
          </div>
          <div>
            <span className="text-gray-500">NO:</span>
            <span className="font-medium text-red-600 ml-1">{(100 - yesPercent).toFixed(1)}%</span>
          </div>
        </div>

        {/* Pool Bar */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex mb-4">
          <div className="bg-green-500" style={{ width: `${yesPercent}%` }} />
          <div className="bg-red-500" style={{ width: `${100 - yesPercent}%` }} />
        </div>

        <div className="text-xs text-gray-500 mb-4">
          Ended: {endDate.toLocaleDateString()} at {endDate.toLocaleTimeString()}
        </div>

        {/* Resolution UI */}
        {!showConfirm ? (
          <div className="flex gap-3">
            <button
              onClick={() => {
                setSelectedOutcome(true);
                setShowConfirm(true);
              }}
              className="btn btn-success flex-1"
            >
              Resolve YES
            </button>
            <button
              onClick={() => {
                setSelectedOutcome(false);
                setShowConfirm(true);
              }}
              className="btn btn-danger flex-1"
            >
              Resolve NO
            </button>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-yellow-800">
                  Confirm Resolution: <span className={selectedOutcome ? "text-green-700" : "text-red-700"}>{selectedOutcome ? "YES" : "NO"}</span>
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  This action is irreversible. Winners will be able to claim their winnings.
                </p>

                {error && (
                  <div className="mt-2 text-sm text-red-600">
                    Error: {error.message}
                  </div>
                )}

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleResolve}
                    disabled={isPending}
                    className={`btn btn-sm ${selectedOutcome ? "btn-success" : "btn-danger"}`}
                  >
                    {isPending ? "Resolving..." : isSuccess ? "Resolved!" : "Confirm"}
                  </button>
                  <button
                    onClick={() => {
                      setShowConfirm(false);
                      setSelectedOutcome(null);
                      reset();
                    }}
                    disabled={isPending}
                    className="btn btn-sm btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveMarketCard({ market }: { market: { address: `0x${string}`; question: string; totalYesBets: bigint; totalNoBets: bigint; endTime: bigint } }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const { closeMarket, isPending, isSuccess, error, reset } = useCloseMarket();

  const totalPool = market.totalYesBets + market.totalNoBets;
  const endDate = new Date(Number(market.endTime) * 1000);

  const handleClose = () => {
    closeMarket(market.address);
  };

  // Reset after success
  if (isSuccess) {
    setTimeout(() => {
      reset();
      setShowConfirm(false);
    }, 2000);
  }

  return (
    <div className="card p-4">
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <Link
            to={`/market/${market.address}`}
            className="font-medium text-gray-900 hover:text-primary-600 truncate block"
          >
            {market.question}
          </Link>
          <div className="text-sm text-gray-500 mt-1">
            Ends: {endDate.toLocaleDateString()} at {endDate.toLocaleTimeString()}
          </div>
          <div className="text-sm text-gray-500">
            Pool: {formatUSDT(totalPool)} USDT
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <span className="badge badge-blue">Active</span>
          {!showConfirm ? (
            <button
              onClick={() => setShowConfirm(true)}
              className="btn btn-sm btn-outline text-yellow-600 border-yellow-600 hover:bg-yellow-50"
            >
              Close Market
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                disabled={isPending}
                className="btn btn-sm btn-warning"
              >
                {isPending ? "Closing..." : isSuccess ? "Closed!" : "Confirm"}
              </button>
              <button
                onClick={() => {
                  setShowConfirm(false);
                  reset();
                }}
                disabled={isPending}
                className="btn btn-sm btn-outline"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
      {error && (
        <div className="mt-2 text-sm text-red-600">
          Error: {error.message}
        </div>
      )}
      {showConfirm && !isPending && !isSuccess && !error && (
        <div className="mt-3 text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
          This will immediately stop all predictions on this market. The market can still be resolved later.
        </div>
      )}
    </div>
  );
}
